import { LiveMobileViewContainer } from "@/components/slides/editor/LiveMobileViewContainer";
import { GradingTestModal } from "@/components/slides/editor/canvas/GradingTestModal";
import { useAppSelector } from "@/store/hooks";
import type { WordVocabularySlideData } from "@ai-teach/types";
import { canAnswerByMic, canAnswerByWriting } from "@ai-teach/utils";
import { useEffect, useRef, useState } from "react";
import { AiFillPicture } from "react-icons/ai";
import { FaMicrophone, FaPencilAlt } from "react-icons/fa";
import { IoPause, IoPlay, IoVolumeHigh } from "react-icons/io5";

export function WordVocabularyLiveMobileView() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const { slide } = useAppSelector((state) => state.slideEditor.present);

  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => () => audioRef.current?.pause(), []);

  if (!slide) return null;

  const wordSlideData = slide.slideData as WordVocabularySlideData;
  const { image, video, audio, instructionText, correctAnswer, mediaType, answerModes } =
    wordSlideData;
  const allowMic = canAnswerByMic(answerModes);
  const allowWriting = canAnswerByWriting(answerModes);
  const isVideoCard = mediaType === "video";
  const hasVideo = isVideoCard && !!video?.src;
  const hasImage = !isVideoCard && !!image.src;
  // Audio only counts in image mode — a video carries its own sound.
  const hasAudio = !isVideoCard && !!audio.src;
  // Audio button only with an image — a video carries its own sound.
  const showAudioButton = hasImage && hasAudio;
  const isAudioOnly = !hasImage && !hasVideo && hasAudio;

  const toggleAudio = () => {
    const a = audioRef.current;
    if (!a || !audio.src) return;
    if (playing) {
      a.pause();
      return;
    }
    a.play().catch((err) => {
      if (err?.name !== "AbortError") setPlaying(false);
    });
  };

  return (
    <LiveMobileViewContainer>
      {/* biome-ignore lint/a11y/useMediaCaption: authoring preview — audio is the content being tested */}
      <audio
        ref={audioRef}
        src={audio.src ?? undefined}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setProgress(0);
        }}
        onTimeUpdate={(e) => {
          const a = e.currentTarget;
          setProgress(a.duration ? a.currentTime / a.duration : 0);
        }}
      />

      {/* Card — top 80% */}
      <div className="w-full h-[80%] flex flex-col items-center justify-center bg-[#FFFEF7] shadow-card">
        <div className="flex flex-col items-center justify-center w-[90%] h-full gap-4 py-4">
          {instructionText && (
            // <p className="text-[#441ce4] text-2xl font-extrabold tracking-wide text-center px-2 leading-tight">
            <p
              style={{
                textAlign: "center",
                paddingLeft: 8,
                paddingRight: 8,
                fontFamily: "KumbhSans-Bold",
                fontSize: 31,
                lineHeight: "40px",
                color: "#000000",
              }}
            >
              {instructionText}
            </p>
          )}

          {isAudioOnly ? (
            playing ? (
              <button
                type="button"
                onClick={toggleAudio}
                className="mt-8 w-[300px] flex items-center gap-3"
              >
                <IoVolumeHigh className="text-[#FFAC27] shrink-0" style={{ fontSize: 40 }} />
                <div className="flex-1 h-2.5 rounded-full bg-[#E5E5E5] overflow-hidden">
                  <div
                    className="h-full rounded-full bg-[#FFAC27] transition-[width] duration-150"
                    style={{ width: `${Math.round(progress * 100)}%` }}
                  />
                </div>
              </button>
            ) : (
              <button
                type="button"
                onClick={toggleAudio}
                className="mt-8 w-[300px] h-[80px] rounded-full flex items-center justify-center bg-[#FFAC27]"
              >
                <IoVolumeHigh className="text-white" style={{ fontSize: 48 }} />
              </button>
            )
          ) : (
            <div
              className={`relative flex items-center justify-center overflow-hidden ${
                hasVideo ? "bg-black" : ""
              }`}
              style={{
                width: "85%",
                aspectRatio: 1,
                maxHeight: "75%",
                borderRadius: 30,
              }}
            >
              {hasVideo ? (
                <video
                  src={video?.src ?? undefined}
                  className="w-full h-full object-contain"
                  controls
                  autoPlay
                  muted
                  loop
                  playsInline
                />
              ) : hasImage ? (
                <>
                  <img
                    src={image.displaySrc ?? image.src ?? undefined}
                    alt={image.src ?? ""}
                    className="w-full h-full object-cover"
                  />
                  {showAudioButton && (
                    <button
                      type="button"
                      onClick={toggleAudio}
                      className="absolute bottom-3 right-3 w-[52px] h-[52px] rounded-full flex items-center justify-center shadow-md transition-opacity hover:opacity-90"
                      style={{
                        backgroundColor: playing ? "#CC6A00" : "#FF8500",
                      }}
                    >
                      {playing ? (
                        <IoPause className="text-xl" color="#FFFEF7" />
                      ) : (
                        <IoPlay className="text-xl" color="#FFFEF7" />
                      )}
                    </button>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-orange-100/10">
                  <AiFillPicture className="text-[#FF8C42] text-5xl" />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bottom — whichever answer buttons the slide allows; both open the grading modal */}
      <div className="w-full h-[20%] flex items-center justify-center gap-5">
        {allowMic && (
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className={`${allowWriting ? "w-[68px] h-[68px]" : "w-20 h-20"} rounded-full flex items-center justify-center`}
            style={{
              background: "linear-gradient(180deg, #FF8500 0%, #FFB800 100%)",
              boxShadow: "0 4px 14px rgba(255, 145, 77, 0.4)",
            }}
          >
            <FaMicrophone size={allowWriting ? 26 : 30} color="white" />
          </button>
        )}
        {allowWriting && (
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className={`${allowMic ? "w-[68px] h-[68px]" : "w-20 h-20"} rounded-full flex items-center justify-center`}
            style={{
              background: "linear-gradient(135deg, #FF8500 0%, #FFB800 100%)",
              boxShadow: "0 4px 14px rgba(255, 145, 77, 0.4)",
            }}
          >
            <FaPencilAlt size={allowMic ? 24 : 28} color="white" />
          </button>
        )}
      </div>

      <GradingTestModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        fields={[
          { label: "Question", value: instructionText },
          { label: "Expected Answer", value: correctAnswer },
        ]}
        question={instructionText ?? ""}
        correctAnswer={correctAnswer}
        toastId="gradingToastVocab"
      />
    </LiveMobileViewContainer>
  );
}
